import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import {
  Heart, Sparkles, Copy, Check, Facebook, Instagram, Tent, Sun,
  MessageCircle, Users, Mountain, Film, ExternalLink, Search, Scale, ShoppingBag,
} from "lucide-react";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import { SOCIAL } from "../lib/social";
import { api } from "../lib/api";

/**
 * /launch-pack — copy-paste central for the founder.
 *
 * Three modules:
 *   1. Friends-&-family Messenger invite (with QR + URLs for FB + IG)
 *   2. Public Facebook page launch posts (3 variants — community vibe)
 *   3. Public Instagram launch posts (3 variants — visual + tappable)
 *
 * Every text block has a "Copy" button. Every link has a QR. Designed so
 * Anthony can flip through, copy, paste into Messenger / FB / IG, and never
 * stare at a blank page wondering "what do I write?"
 *
 * Templates use `{COUNT}` and `{LAUNCH_DATE}` placeholders which are
 * substituted with the live campsite count (from /api/campsites/stats) and
 * the configured launch date at render time. So the copy is always current
 * without any code edits.
 */
const PUBLIC_URL =
  (process.env.REACT_APP_PUBLIC_URL || "").trim().replace(/\/$/, "") ||
  "https://camp-compass.uk";

const LAUNCH_DATE = new Date("2026-05-31T00:00:00Z");
const LAUNCH_LABEL = LAUNCH_DATE.toLocaleDateString("en-GB", {
  day: "numeric", month: "long", year: "numeric",
});

// ---------------------------------------------------------------------------
// Copy templates — PRE-LAUNCH teasers. The goal is page follows + buzz —
// not website traffic yet. Website link is held back deliberately so the
// reveal lands hard on launch day.
// ---------------------------------------------------------------------------
const MESSENGER_INVITE = `Hey 👋

Got something I've been working on for a while that I'd love your help with.

It's called CampCompass UK 🏕️ — a brand-new home for UK campers, going LIVE on {LAUNCH_DATE}. The honest campsite directory we've all been waiting for: {COUNT} sites compared side-by-side, real prices, real reviews, no sponsored rankings. And a kind, moderated community where every camper — first-timers, families, retirees, tourers, dog people, festival folk — gets a thoughtful answer to every question.

The app is already on your phone in 3 taps — and you can create your account RIGHT NOW so you're ready the second we launch. I'd be over the moon if you'd:

📲 Install + sign up today (60 seconds): ${PUBLIC_URL}
🟦 Like our Facebook ${SOCIAL.facebook.url}
📸 Follow on Instagram ${SOCIAL.instagram.url}

Every camper who signs up gets a 14-day free trial of Pro — every feature unlocked, cancel anytime. After that, share with one camping mate so they don't miss out either.

Thank you for being part of the start of this 🙏
— Anthony`;

const FB_POSTS = [
  {
    title: "Pre-launch announcement (post first)",
    body: `Coming soon · CampCompass UK 🏕️

For 18 months we've been quietly building the campsite directory the UK has been crying out for: {COUNT} sites compared side-by-side. Real prices, real reviews, no sponsored rankings. A kind, moderated camper community where the Campfire Code is what matters — be kind, share knowledge, lift each other up.

We're nearly ready.

→ Tap "Follow" so you don't miss launch day
→ Tag a camping mate in the comments
→ Drop your favourite UK pitch — we'll feature the best ones in our launch carousel

For first-timers, lifelong tourers, families with toddlers, retirees with caravans, solo wild-pitchers, dog people, festival folk. Everyone fits round our campfire.

${SOCIAL.hashtag} #UKCamping #CamperCommunity #ComingSoon`,
  },
  {
    title: "Sneak-peek thread tease (post mid-week)",
    body: `Sneak peek of the conversations we're going to start 👇

📍 "First tent trip with a 3-year-old next month — Lake District. Anyone got a recommendation for somewhere quiet, with proper toilets, where I won't lose her in the dark?"

Imagine getting:
→ 3 specific site recommendations from real campers
→ 2 packing-list tips from parents who've been there
→ A retired camper offering to lend you a spare windbreak

No sales pitches. No paid placements. Just neighbours.

THAT'S CampCompass UK 🏕️

We launch soon. Follow this page so you're first in.

${SOCIAL.hashtag}`,
  },
  {
    title: "Community-first hook (post end of week)",
    body: `Most camping apps treat you like a wallet.

We're going to treat you like a neighbour 🏕️

CampCompass UK launches soon. Built BY campers FOR campers — every age, every kit, every level. The Campfire Code keeps it warm: be kind, share knowledge, lift each other up.

Whether you're a first-timer working out which tent peg goes where, or you've been touring since the 80s — there'll be a thread waiting for you.

🏷️ Tap follow now → every signup gets a 14-day free trial of Pro when we launch (every feature unlocked, cancel anytime).

Tag a camping pal who'd love this 👇

${SOCIAL.hashtag}`,
  },
];

const IG_POSTS = [
  {
    title: "Pre-launch hero post (use orange-tent-sunrise photo)",
    body: `Camping is for all of us 🏕️

Coming soon · CampCompass UK ✨

We've been quietly building the UK's honest camper community — for first-timers, families, retirees, tourers, solo wild-pitchers, dog people, festival folk. Everyone fits round our campfire 🔥

✦ {COUNT} UK campsites compared
✦ Real prices · real reviews · zero sponsored rankings
✦ AI Camper free for everyone
✦ A kind, moderated community where every question gets a thoughtful answer

We're not live yet. But the page is open.

Tap follow 👆 so you're first invited in on launch day.

Tag a camping mate · drop your favourite UK pitch in the comments 👇

${SOCIAL.hashtag} #ComingSoon #UKCamping #CamperCommunity #SlowTravel #OutdoorsUK #TentLife #CaravanLife #FamilyCamping`,
  },
  {
    title: "Community vibe teaser (carousel of mock threads)",
    body: `A peek at the questions we're going to answer 👇

🏕️ "Lake District in October — sleeping bag rating?"
🐕 "Best dog-friendly site in Pembrokeshire?"
🚐 "First-time campervan tour — what would you skip?"
🌧️ "How do you keep boots dry overnight in a tent?"

Every one will get 5+ thoughtful replies from real campers. No sales pitches. No ads. Just neighbours.

This is what we're building — and we'd love you in it.

🟠 Tap follow · launch day is close.

${SOCIAL.hashtag}`,
  },
  {
    title: "Quiet visual post (orange-tent-sunrise photo, low-key)",
    body: `When the sun comes up and the kettle's on. ☀️🫖

That's why we camp.

CampCompass UK · the honest UK camper community · launching soon. Tap follow 👆

${SOCIAL.hashtag} #SunriseCamping #UKCamping #SlowMornings #CamperCommunity`,
  },
];

// ---------------------------------------------------------------------------
// LAUNCH DAY — "we're live!" posts. Use these the morning of launch day.
// Now the website link DOES appear (this is the reveal moment).
// ---------------------------------------------------------------------------
const LAUNCH_FB_POSTS = [
  {
    title: "Launch announcement (post 9am)",
    body: `🏕️ It's here.

After 18 months of quiet building, CampCompass UK is LIVE.

→ ${PUBLIC_URL}

We're not another booking engine. We're a kind, moderated camper community for ALL ages and every kit. First-time tent-pitchers, lifelong tourers, families with toddlers, retirees with caravans, solo wild-pitchers, dog people, festival folk — everyone fits round our campfire.

What you get from day one:
✅ {COUNT} UK campsites compared side-by-side
✅ Real prices · real reviews · zero sponsored rankings
✅ AI Camper for free packing/weather advice
✅ A community where every question gets a thoughtful answer
✅ £1.99/month Pro · 14 days free · cancel anytime

To everyone who's followed, shared, and waited — thank you. Today is yours too.

Share this post with one camping mate. Pop a campsite memory in the comments. Let's start the warmest community in UK camping. 🔥

${SOCIAL.hashtag} #UKCamping #CamperCommunity`,
  },
  {
    title: "Founder thank-you (post 6pm same day)",
    body: `Today CampCompass UK went live → ${PUBLIC_URL}

I'm writing this from my kitchen table. The same one I started sketching ideas on a year and a half ago. And I just want to say one thing:

Thank you 🙏

To every person who tapped follow before launch. To every camper who said "this sounds brilliant" and then proved it by sharing the page. To my family, who watched me disappear into a laptop for evenings on end. To every camper trying us out — you're part of the story now.

This isn't done. It's just begun. Tomorrow we keep building it WITH you.

If you've not had a look yet — go gentle, it's a kitchen-table operation, but it's ours: ${PUBLIC_URL}

— Anthony

${SOCIAL.hashtag}`,
  },
];

const LAUNCH_IG_POSTS = [
  {
    title: "Launch carousel (use orange-tent + screenshots — see below)",
    body: `It's here 🏕️

CampCompass UK is LIVE → link in bio

The honest UK camper community we've all been waiting for:
✦ {COUNT} campsites compared
✦ Real prices · real reviews · NO sponsored rankings
✦ Free AI Camper for packing & weather
✦ A kind, moderated community for ALL ages and every kit
✦ £1.99/month Pro · 14 days free trial · every feature unlocked, cancel anytime

Whether you're a first-timer working out which tent peg goes where, or you've been touring since the 80s — there's a thread waiting for you.

Tag a camping mate · save for your next trip · share to your story 👇

${SOCIAL.hashtag} #LaunchDay #UKCamping #CamperCommunity #SlowTravel #OutdoorsUK #TentLife #CaravanLife #FamilyCamping`,
  },
  {
    title: "Launch story sequence (3 stories — use as Instagram Stories)",
    body: `STORY 1 (orange-tent sunrise photo, full-bleed):
"Today's the day 🏕️"
+ link sticker → camp-compass.uk

STORY 2 (screenshot of Browse page with map + cards):
"{COUNT} UK sites · all in one place · no sponsored rankings"
+ link sticker → camp-compass.uk

STORY 3 (screenshot of a Community thread):
"Real questions. Real campers. Real answers."
+ link sticker → camp-compass.uk

(Use the in-app screenshots from the Screenshot Kit below.)`,
  },
];

// ---------------------------------------------------------------------------
// WEEK 1 — reflection posts. Use these 5–7 days after launch to keep momentum
// going without hard-selling.
// ---------------------------------------------------------------------------
const WEEK1_FB_POSTS = [
  {
    title: "Week 1 reflection (post Sunday evening)",
    body: `One week in 🏕️

Some numbers from CampCompass UK's first 7 days:
→ [N] new campers joined
→ [N] questions asked in the community
→ [N] camp memories shared
→ [N] price corrections from real campers

But the number that stuck with me: every single question got an answer. Not from a bot. Not from a sales team. From other campers.

That's the whole game. Be useful. Be kind. Lift each other up.

If you've not joined yet, you're welcome any time → ${PUBLIC_URL}

If you have, share with one more camping mate this week 🙏

${SOCIAL.hashtag}`,
  },
  {
    title: "Best community moment of the week (post mid-week 2)",
    body: `Thread of the week 👇

[Quote a real, kind, useful exchange from your community here. Replace the placeholder below.]

📍 [Camper A]: "First-time campervan tour, kids 6 and 9, two weeks in August. What would you skip?"

[Camper B]: "Skip the obvious motorway-stop sites. Book one or two 'destination' nights at proper sites with playgrounds, then mix in farm sites between. Kids need a 'home base' every 3 days max — they're more tired than you think."

This is what we're building 🏕️

CampCompass UK · ${PUBLIC_URL} · the honest UK camper community.

${SOCIAL.hashtag}`,
  },
];

const WEEK1_IG_POSTS = [
  {
    title: "Week 1 thank-you (post Sunday)",
    body: `One week in 🏕️🫶

To every camper who joined, posted, replied, or just liked a post — thank you.

CampCompass UK is becoming exactly what we hoped: a kind, useful, honest place to plan trips and meet other campers.

If you've not had a look yet → link in bio.
If you have → tag one camping mate this week who'd love it.

${SOCIAL.hashtag} #WeekOne #UKCamping #CamperCommunity`,
  },
  {
    title: "Mid-week recap (carousel of best community moments)",
    body: `Some quiet wins from this week 👇

→ A first-time tenter pitched her own tent for the first time in the Lake District. The community helped her pack 🏕️
→ A retired couple booked their first campervan tour using 6 site recs from the Wales thread.
→ A solo wild-pitcher in Scotland got 3 hidden-gem suggestions for the Cairngorms.

This is what neighbours look like.

CampCompass UK · link in bio.

${SOCIAL.hashtag} #UKCamping #SlowTravel #CamperCommunity`,
  },
];

// ---------------------------------------------------------------------------
// SCREENSHOT KIT — a checklist of in-app shots to capture for social posts.
// User takes the screenshot themselves (live app) and pairs with the suggested
// caption. Far more authentic than mockups.
// ---------------------------------------------------------------------------
const SCREENSHOT_KIT = [
  {
    n: 1,
    where: "/ (Landing page hero)",
    crop: "Phone screenshot · top of page · capture from notch to just below 'support each other'",
    caption: `"This is how we welcome every camper 🏕️"

— First-time tent-pitchers, families, retirees, tourers, dog people, festival folk. Everyone fits round our campfire.

CampCompass UK · link in bio. ${SOCIAL.hashtag}`,
  },
  {
    n: 2,
    where: "/browse (campsite grid + map)",
    crop: "Desktop screenshot at 1366×768 · show the map at top + at least 4 campsite cards beneath",
    caption: `{COUNT} UK campsites. Compared side-by-side. No sponsored rankings.

This is the directory we've been waiting for.

CampCompass UK · link in bio. ${SOCIAL.hashtag} #UKCamping`,
  },
  {
    n: 3,
    where: "/community/threads (a real conversation)",
    crop: "Desktop screenshot · pick a thread with at least 3 thoughtful replies · capture the question + first 2 replies",
    caption: `Real questions. Real campers. Real answers.

No bots. No sales pitches. Just neighbours.

CampCompass UK · link in bio. ${SOCIAL.hashtag}`,
  },
  {
    n: 4,
    where: "Any campsite detail page (e.g. /campsites/cs_xxx)",
    crop: "Phone screenshot · capture from the photo down to the price + 'Verified by N campers' pill",
    caption: `Crowdsourced prices. Verified by real campers, not scrapers.

When prices change, our campers tell us. That's how the directory stays honest.

CampCompass UK · link in bio. ${SOCIAL.hashtag}`,
  },
  {
    n: 5,
    where: "/wall (Trip Wall photos)",
    crop: "Desktop screenshot · capture 4-6 trip story cards in a tile",
    caption: `The wall is where memories live 🏕️

Pitch up · share your trip · we re-feature the warmest stories every Friday.

CampCompass UK · link in bio. ${SOCIAL.hashtag}`,
  },
  {
    n: 6,
    where: "Chatbot (open the AI Camper widget bottom-right)",
    crop: "Phone screenshot · show the chat panel with a kind, useful answer",
    caption: `Ask anything 🏕️ AI Camper is free for everyone.

"Lake District in October — sleeping bag rating?" → got a thoughtful answer in 2 seconds.

CampCompass UK · link in bio. ${SOCIAL.hashtag}`,
  },
];

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------
function CopyButton({ value, testid }) {
  const [done, setDone] = useState(false);
  const click = async () => {
    try { await navigator.clipboard.writeText(value); }
    catch { /* clipboard may be blocked — silently no-op */ }
    setDone(true);
    setTimeout(() => setDone(false), 1800);
  };
  return (
    <button
      onClick={click}
      data-testid={testid}
      className={`mono text-[10px] uppercase tracking-[0.16em] px-3 py-2 inline-flex items-center gap-1.5 transition-all border ${done ? "bg-[#0d6e1c] text-white border-[#0d6e1c]" : "bg-white text-[#111] border-[#111] hover:bg-[#111] hover:text-white"}`}
    >
      {done ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {done ? "Copied" : "Copy"}
    </button>
  );
}

function QRBlock({ value, label, icon: Icon, testid }) {
  return (
    <div className="border-2 hairline-strong p-4 bg-white flex flex-col items-center text-center" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-3 text-[#ff4500]">
        {Icon && <Icon className="w-4 h-4" strokeWidth={1.7} />}
        <span className="eyebrow !text-[#ff4500]">{label}</span>
      </div>
      <QRCodeSVG value={value} size={140} level="M" bgColor="#ffffff" fgColor="#111111" />
      <div className="mono text-[10px] mt-3 text-[#444] truncate w-full">{value.replace(/^https?:\/\//, "")}</div>
    </div>
  );
}

function PostCard({ title, body, platform, testid }) {
  const PlatformIcon = platform === "facebook" ? Facebook : platform === "instagram" ? Instagram : MessageCircle;
  const accent = platform === "facebook" ? "#1877f2" : platform === "instagram" ? "#e1306c" : "#ff4500";
  return (
    <article className="border-2 hairline-strong bg-white" data-testid={testid}>
      <header className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b hairline">
        <div className="flex items-center gap-2">
          <PlatformIcon className="w-4 h-4" strokeWidth={1.8} style={{ color: accent }} />
          <h3 className="font-display text-lg leading-tight">{title}</h3>
        </div>
        <CopyButton value={body} testid={`${testid}-copy`} />
      </header>
      <pre className="px-5 py-4 whitespace-pre-wrap font-sans text-[14px] leading-[1.6] text-[#222] max-w-full overflow-x-auto">
{body}
      </pre>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function LaunchPack() {
  // Live campsite count so every copy-paste block stays current automatically.
  const [count, setCount] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/campsites/stats").then(({ data }) => {
      if (alive && typeof data?.total === "number") setCount(data.total);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const countLabel = count != null ? `${count.toLocaleString()}+` : "11,000+";

  // Substitute placeholders in a template string with live values.
  // Templates use `{COUNT}` for catalogue size, `{LAUNCH_DATE}` for the
  // public launch day. Keeps copy current without code edits.
  const fill = useMemo(() => (text) => {
    if (!text) return text;
    return text
      .replaceAll("{COUNT}", countLabel)
      .replaceAll("{LAUNCH_DATE}", LAUNCH_LABEL);
  }, [countLabel]);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Header />

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-12" data-testid="launch-pack">
        {/* Intro */}
        <header>
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Marketing toolkit · soft-launch
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2 leading-[0.95]">
            Your launch pack 🏕️
          </h1>
          <p className="text-[16px] text-[#444] mt-4 leading-relaxed max-w-2xl">
            Pre-launch teasers to build buzz on Facebook and Instagram before
            launch day (<strong className="text-[#ff4500]">{LAUNCH_LABEL}</strong>). Goal: <strong className="text-[#111]">page follows + early
            community sign-ups</strong>. The Messenger invite + every post invites people
            to <strong>install + create an account now</strong>, so they're ready the
            second we launch. Tap <strong>Copy</strong> on any block, paste into Messenger
            / FB / IG, hit send.
          </p>
          <p className="mono text-[11px] text-[#888] mt-3">
            Live data: catalogue is auto-refreshed to <strong className="text-[#111]">{countLabel}</strong> campsites · launch date stamped from <code>LAUNCH_DATE</code>.
          </p>
        </header>

        {/* MODULE 1 — Friends & Family invite */}
        <section className="space-y-5">
          <SectionHeading
            icon={Heart}
            eyebrow="Module 1 · personal invite"
            title="Friends & family · Messenger"
            subtitle="Send this in a private message to anyone you'd love to have on board from day one. Warm, no hard sell, no spam."
          />

          <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-start">
            {/* Copy block */}
            <article className="border-2 hairline-strong bg-white">
              <header className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b hairline">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-[#ff4500]" strokeWidth={1.8} />
                  <h3 className="font-display text-lg leading-tight">Personal invite (paste into Messenger / WhatsApp / SMS)</h3>
                </div>
                <CopyButton value={fill(MESSENGER_INVITE)} testid="copy-messenger-invite" />
              </header>
              <pre className="px-5 py-4 whitespace-pre-wrap font-sans text-[14px] leading-[1.6] text-[#222] max-w-full overflow-x-auto">
{fill(MESSENGER_INVITE)}
              </pre>
            </article>

            {/* QR side panel — only the two social pages. The website QR is
                deliberately NOT shown pre-launch so the reveal lands hard. */}
            <aside className="space-y-4">
              <QRBlock value={SOCIAL.facebook.url} label="Facebook page" icon={Facebook} testid="qr-fb" />
              <QRBlock value={SOCIAL.instagram.url} label="Instagram page" icon={Instagram} testid="qr-ig" />
            </aside>
          </div>
        </section>

        {/* MODULE 2 — Facebook page posts */}
        <section className="space-y-5">
          <SectionHeading
            icon={Facebook}
            eyebrow="Module 2 · Facebook page"
            title="Public posts for your Facebook page"
            subtitle="Three ready-to-post variants: a launch announcement, a community-first hook, and a sneak-peek thread tease. Post one each week to keep momentum."
          />
          <div className="space-y-5">
            {FB_POSTS.map((p, i) => (
              <PostCard
                key={i}
                title={p.title}
                body={fill(p.body)}
                platform="facebook"
                testid={`fb-post-${i}`}
              />
            ))}
          </div>
        </section>

        {/* MODULE 3 — Instagram posts */}
        <section className="space-y-5">
          <SectionHeading
            icon={Instagram}
            eyebrow="Module 3 · Instagram"
            title="Public posts for your Instagram page"
            subtitle="Three Instagram-ready captions. Pair the visual hero post with the orange-tent-sunrise photo (`/brand/poster-hero-v2.jpg`). Post via the Instagram app — paste the caption, attach the photo, hit share."
          />
          <div className="space-y-5">
            {IG_POSTS.map((p, i) => (
              <PostCard
                key={i}
                title={p.title}
                body={fill(p.body)}
                platform="instagram"
                testid={`ig-post-${i}`}
              />
            ))}
          </div>
        </section>

        {/* MODULE 4 — LAUNCH DAY */}
        <section className="space-y-5">
          <SectionHeading
            icon={Sparkles}
            eyebrow="Module 4 · launch day"
            title={`"We're live" — post these the morning of launch`}
            subtitle="The reveal. Now the website link goes public. Post the launch announcement on Facebook at 9am, the Instagram launch carousel at 10am, the founder thank-you at 6pm, and run the Story sequence in parallel."
          />
          <div className="space-y-5">
            {LAUNCH_FB_POSTS.map((p, i) => (
              <PostCard key={`launch-fb-${i}`} title={p.title} body={fill(p.body)} platform="facebook" testid={`launch-fb-${i}`} />
            ))}
            {LAUNCH_IG_POSTS.map((p, i) => (
              <PostCard key={`launch-ig-${i}`} title={p.title} body={fill(p.body)} platform="instagram" testid={`launch-ig-${i}`} />
            ))}
          </div>
        </section>

        {/* MODULE 5 — WEEK 1 REFLECTIONS */}
        <section className="space-y-5">
          <SectionHeading
            icon={Heart}
            eyebrow="Module 5 · week 1 reflections"
            title="Keep momentum without hard-selling"
            subtitle="Post 5–7 days after launch. Numbers in [brackets] are placeholders — fill them in from your real Admin stats. The 'Best community moment' post is gold for retention; replace the placeholder with a real exchange from your threads."
          />
          <div className="space-y-5">
            {WEEK1_FB_POSTS.map((p, i) => (
              <PostCard key={`w1-fb-${i}`} title={p.title} body={fill(p.body)} platform="facebook" testid={`w1-fb-${i}`} />
            ))}
            {WEEK1_IG_POSTS.map((p, i) => (
              <PostCard key={`w1-ig-${i}`} title={p.title} body={fill(p.body)} platform="instagram" testid={`w1-ig-${i}`} />
            ))}
          </div>
        </section>

        {/* MODULE 6 — SCREENSHOT KIT */}
        <section className="space-y-5">
          <SectionHeading
            icon={Tent}
            eyebrow="Module 6 · screenshot kit"
            title="Six in-app shots to capture (with paired captions)"
            subtitle="Take these on a real device — they're far more authentic than designed mockups. Each row tells you exactly what to capture and gives you the caption to pair it with. Post one per day for a week and you've got a fortnight of feed content."
          />
          <div className="space-y-5">
            {SCREENSHOT_KIT.map((s) => (
              <article
                key={s.n}
                className="border-2 hairline-strong bg-white p-5 grid md:grid-cols-[80px_1fr_auto] gap-4 items-start"
                data-testid={`screenshot-${s.n}`}
              >
                <div className="flex items-center justify-center w-16 h-16 bg-[#ff4500] text-white font-display text-3xl">{s.n}</div>
                <div>
                  <div className="eyebrow text-[#ff4500]">Screenshot {s.n}</div>
                  <h3 className="font-display text-lg mt-1 leading-tight">{s.where}</h3>
                  <div className="mono text-[11px] text-[#666] mt-1">{s.crop}</div>
                  <pre className="whitespace-pre-wrap font-sans text-[13px] leading-[1.55] text-[#222] mt-3 bg-[#fafafa] border hairline px-3 py-2.5">
{fill(s.caption)}
                  </pre>
                </div>
                <CopyButton value={fill(s.caption)} testid={`screenshot-${s.n}-copy`} />
              </article>
            ))}
          </div>
        </section>

        {/* Reels library — every share-able reel in one place. iter128. */}
        <section className="space-y-5" data-testid="lp-reels-library">
          <SectionHeading
            icon={Film}
            eyebrow="One-tap marketing"
            title="Reels library — open, copy caption, share"
            subtitle="Every 9:16 share-able reel routed from the app. Tap a card to open the reel page, copy its pre-written caption, screen-record / screenshot-stitch into an MP4, and post to Instagram / TikTok / Facebook in under a minute."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* iter189 — 4 fresh signup-conversion + Teva OUTLET15 reels
                pinned at the top of the library so ops sees them first. */}
            <ReelTile
              path="/share/unlock-everything"
              title="Unlock everything · free"
              tagline="Sign-up wall pitch · 3 scenes"
              icon={Sparkles}
              fresh
            />
            <ReelTile
              path="/share/why-join-now"
              title="Why join now"
              tagline="Urgency · paid tiers this week · 3 scenes"
              icon={Sparkles}
              fresh
            />
            <ReelTile
              path="/share/teva-outlet"
              title="Teva UK · OUTLET15 · 15% off"
              tagline="Ends 28 Sep 2026 · affiliate reel · 3 scenes"
              icon={ShoppingBag}
              fresh
            />
            <ReelTile
              path="/share/locked-features"
              title="Locked features · what you're missing"
              tagline="Grey-out reveal · 3 scenes"
              icon={ShoppingBag}
              fresh
            />
            {/* iter184 — Freshly-shipped conversion reels. Keep at the top so ops sees them first. */}
            <ReelTile
              path="/share/partner-shops"
              title="Partner shops · gear hub"
              tagline="Real affiliates (Trek Expert, FINDRA, Absolute Snow…) · 3 scenes"
              icon={ShoppingBag}
              fresh
            />
            <ReelTile
              path="/share/vote-on-poll"
              title="Vote on the weekly poll"
              tagline="One-tap Facebook vote + free signup · 3 scenes"
              icon={Sparkles}
              fresh
            />
            <ReelTile
              path="/share/share-a-trip"
              title="Share a trip or photo"
              tagline="Two-tap community wall onboarding · 3 scenes"
              icon={MessageCircle}
              fresh
            />
            <ReelTile
              path="/share/how-it-works"
              title="How it works · 15-sec walkthrough"
              tagline="Simplified 4-step + signup push · 3 scenes"
              icon={Sparkles}
              fresh
            />
            <ReelTile
              path="/share/simplified-search"
              title="Simplified search & compare"
              tagline="Hook the struggling-to-find crowd · 4 scenes"
              icon={Search}
              fresh
            />
            <ReelTile path="/share/share-your-trip" title="Share your trip" tagline="Funnels into the Campfire community · 4 scenes" icon={MessageCircle} />
            <ReelTile path="/share/free-vs-pro" title="Free vs Pro" tagline="Conversion-driver · 6 scenes" icon={Sparkles} />
            <ReelTile path="/share/try-pro-free" title="Try Pro free · 14 days" tagline="Trial nudge · 4 scenes" icon={Sparkles} />
            <ReelTile path="/share/join-the-chat" title="Join the Campfire chat" tagline="Community pitch · 4 scenes" icon={MessageCircle} />
            <ReelTile path="/share/launch-day" title="Launch day" tagline="Day-of-launch hype · 5 scenes" icon={Heart} />
            <ReelTile path="/share/final-call" title="Final call" tagline="Last-minute push · 3 scenes" icon={Heart} />
            <ReelTile path="/share/mountain-rescue" title="Mountain Rescue charity" tagline="Donation drive · 4 scenes" icon={Mountain} />
            <ReelTile path="/share/autumn-camping" title="Autumn camping" tagline="Sign-up push · dry-stretch angle · 4 scenes" icon={Sparkles} />
            <ReelTile path="/share/august-warm-weather" title="August warm weather" tagline="Facebook push · bright + vibrant · 4 scenes" icon={Sun} />
            <ReelTile path="/share/festival-relief" title="Festival relief" tagline="Festival-goer rescue · 4 scenes" icon={Tent} />
            {/* iter131 — Dulas Glamping tile removed per user request. */}
            <ReelTile path="/share/frowen-fields" title="Frowen Fields partner" tagline="Partner spotlight · 5 scenes" icon={Tent} />
            <ReelTile path="/share/campsite-promo" title="Generic campsite promo" tagline="Reusable template · 4 scenes" icon={Tent} />
            <ReelTile path="/share/fishing-camping" title="Fishing × camping" tagline="Niche segment · 4 scenes" icon={Tent} />
          </div>
        </section>

        {/* Sneak-peek mock threads — community-vibe screenshots */}
        <section className="space-y-5">
          <SectionHeading
            icon={Users}
            eyebrow="Bonus · screenshot bait"
            title="Mock community threads (use as image posts)"
            subtitle="Take a screenshot of any of these mock threads, drop into an Instagram story / FB post with caption: 'A peek at the conversations we're starting on CampCompass UK 👇'. Shows the vibe better than any pitch."
          />

          <div className="grid md:grid-cols-2 gap-5">
            <MockThread
              title="First tent trip with a toddler — recs?"
              author="Hannah · joined this week"
              tag="Lake District"
              icon={Tent}
              replies={[
                { who: "Margaret · 12 years touring", text: "Park Coppice on Coniston is brilliant for littles — flat pitches, proper toilets, lakeside walks. Avoid bank holidays." },
                { who: "Dan · campervan dad", text: "+1 for Park Coppice. Also Castlerigg Hall has a pub down the road and the night sky is unreal. Take a windbreak." },
                { who: "Kit · retired ranger", text: "If she's nervous in the dark, head-torches with red light setting are a winner. I've got a spare you can borrow." },
              ]}
            />
            <MockThread
              title="Pembrokeshire dog-friendly?"
              author="Tom · weekend tourer"
              tag="Wales"
              icon={Mountain}
              replies={[
                { who: "Sara · spaniel mum", text: "Caerfai Bay above the cliffs — dogs free, beach access, owners chat at dusk. Magic." },
                { who: "Pete · year-round wild", text: "Newgale Camping Site if you want surf-walking distance. Bring waterproofs whatever the forecast 😅" },
                { who: "Lou · CC mod", text: "Both pinned in the Wales thread — see /community/threads. Welcome aboard 🏕️" },
              ]}
            />
          </div>
        </section>

        {/* Quick links footer */}
        <section className="border-t hairline pt-8">
          <div className="flex flex-wrap gap-3">
            <Link to="/print/poster" className="text-sm underline hover:text-[#ff4500]" data-testid="lp-poster-link">
              → A4 poster (print pack)
            </Link>
            <Link to="/print/leaflet" className="text-sm underline hover:text-[#ff4500] ml-3" data-testid="lp-leaflet-link">
              → A5 leaflet (NEW · iter121 · updated copy)
            </Link>
            <span className="text-[#ccc]">·</span>
            <Link to="/print/cards" className="text-sm underline hover:text-[#ff4500]" data-testid="lp-cards-link">
              → Business cards (print pack)
            </Link>
            <span className="text-[#ccc]">·</span>
            <Link to="/admin" className="text-sm underline hover:text-[#ff4500]" data-testid="lp-admin-link">
              → Back to Admin
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function ReelTile({ path, title, tagline, icon: Icon, fresh = false }) {
  const [copied, setCopied] = useState(false);
  const PUBLIC = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
  const fullUrl = `${PUBLIC}${path}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(fullUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* ignore — clipboard may be blocked */ }
  };
  const _testKey = path.replace(/\W+/g, "-");
  return (
    <div className="border hairline rounded-xl p-3 bg-white flex flex-col gap-2 hover:border-[#ff4500] transition-colors" data-testid={`lp-reel-tile-${_testKey}`}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#fff4ee] flex items-center justify-center shrink-0">
          {Icon && <Icon className="w-4 h-4 text-[#ff4500]" strokeWidth={2.2} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm leading-tight flex items-center gap-1.5">
            <span className="truncate">{title}</span>
            {fresh && <span className="mono text-[8px] uppercase tracking-wider bg-[#ff4500] text-white px-1.5 py-0.5 rounded-full font-bold shrink-0">New</span>}
          </div>
          <div className="mono text-[10px] text-[#888] uppercase tracking-wider truncate">{tagline}</div>
        </div>
      </div>
      <div className="flex gap-1.5 mt-1">
        <Link
          to={path}
          target="_blank"
          rel="noreferrer"
          className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-[#ff4500] hover:bg-[#e63e00] text-white text-[10px] font-bold uppercase tracking-wider"
          data-testid={`lp-reel-open-${_testKey}`}
        >
          Open <ExternalLink className="w-2.5 h-2.5" />
        </Link>
        <button
          type="button"
          onClick={copy}
          aria-label="Copy reel URL"
          className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-white border border-[#ddd] hover:border-[#ff4500] text-[#555] hover:text-[#ff4500] text-[10px] font-bold uppercase tracking-wider"
          data-testid={`lp-reel-copy-${_testKey}`}
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        </button>
      </div>
    </div>
  );
}


function SectionHeading({ icon: Icon, eyebrow, title, subtitle }) {
  return (
    <div className="border-l-4 border-[#ff4500] pl-5">
      <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}
        {eyebrow}
      </div>
      <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-1">{title}</h2>
      {subtitle && <p className="text-sm text-[#555] mt-2 leading-relaxed max-w-2xl">{subtitle}</p>}
    </div>
  );
}

function MockThread({ title, author, tag, icon: Icon, replies }) {
  return (
    <article className="border-2 hairline-strong bg-white p-5">
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.16em] text-[#ff4500]">
            {Icon && <Icon className="w-3 h-3" />}
            {tag}
          </div>
          <h3 className="font-display text-lg leading-tight mt-1">{title}</h3>
          <div className="mono text-[11px] text-[#888] mt-1">{author}</div>
        </div>
        <div className="mono text-[11px] text-[#444] bg-[#fff4ee] px-2 py-1">
          {replies.length} replies
        </div>
      </header>
      <div className="space-y-3 border-l-2 border-[#ff4500] pl-3">
        {replies.map((r, i) => (
          <div key={i}>
            <div className="mono text-[10px] uppercase tracking-[0.14em] text-[#888]">{r.who}</div>
            <p className="text-[13px] text-[#222] leading-snug mt-0.5">{r.text}</p>
          </div>
        ))}
      </div>
    </article>
  );
}
